// AIDEV-NOTE: Edgetag (Blotout) plugin for the `analytics` package abstraction.
// Maps the analytics package's page() and track() calls to Edgetag standard events.
// Standard events covered: PageView, ViewContent, AddToCart, InitiateCheckout, Lead, Purchase, Subscribe.
// Reference: https://docs.edgetag.io/overview/standard-events
// AIDEV-NOTE: This file used to live at apps/subflow/src/lib/edgetagPlugin.ts; moved into the
// shared @petplate/analytics package so apps/web can wire the same plugin without duplication.
import type { AnalyticsPlugin } from 'analytics';

declare global {
  interface Window {
    edgetag?: EdgetagStub;
  }
}

type EdgetagStub = ((...args: unknown[]) => void) & {
  stubs?: unknown[][];
};

interface EdgetagPluginConfig {
  edgeURL: string;
  disableConsentCheck?: boolean;
}

// AIDEV-NOTE: Track-event names accepted by the analytics package, normalised to Edgetag standard event names.
// Devs can call analytics.track('AddToCart', {...}) OR analytics.track('add_to_cart', {...}) — both map to Edgetag's 'AddToCart'.
const STANDARD_EVENT_MAP: Record<string, string> = {
  pageview: 'PageView',
  view_content: 'ViewContent',
  viewcontent: 'ViewContent',
  add_to_cart: 'AddToCart',
  addtocart: 'AddToCart',
  initiate_checkout: 'InitiateCheckout',
  initiatecheckout: 'InitiateCheckout',
  lead: 'Lead',
  purchase: 'Purchase',
  subscribe: 'Subscribe'
};

const toEdgetagEvent = (event: string): string | null => {
  const key = event.toLowerCase().replace(/\s+/g, '_');
  return STANDARD_EVENT_MAP[key] ?? null;
};

export const edgetagPlugin = (config: EdgetagPluginConfig): AnalyticsPlugin => ({
  name: 'edgetag',
  config,
  initialize: () => {
    if (typeof window === 'undefined' || window.edgetag) return;

    const stub: EdgetagStub = function (...args: unknown[]) {
      stub.stubs = stub.stubs ?? [];
      stub.stubs.push(args);
    };
    window.edgetag = stub;

    window.edgetag('init', {
      edgeURL: config.edgeURL,
      disableConsentCheck: config.disableConsentCheck ?? true
    });

    const script = document.createElement('script');
    script.async = true;
    script.type = 'text/javascript';
    script.src = `${config.edgeURL}/load`;
    document.head.appendChild(script);
  },
  page: () => {
    if (typeof window === 'undefined') return;
    window.edgetag?.('tag', 'PageView');
  },
  track: ({ payload }: { payload: { event: string; properties?: Record<string, unknown> } }) => {
    if (typeof window === 'undefined') return;
    const standardEvent = toEdgetagEvent(payload.event);
    if (!standardEvent) return;
    window.edgetag?.('tag', standardEvent, payload.properties ?? {});
  },
  // AIDEV-NOTE: window guard is mandatory — `loaded` is invoked during Next.js SSR / prerender
  // to determine plugin readiness. Without the guard, apps/web build crashes with
  // "ReferenceError: window is not defined" during static generation.
  loaded: () => typeof window !== 'undefined' && Boolean(window.edgetag)
});